import React from 'react';

import { HN_SKJEMA_FRONTEND_RESOURCES } from 'src/constants/constants';
import { useResources } from 'src/resources';
import { useAppSelector } from 'src/store/reducers';
import { getSkjemaNameFromUrl } from 'src/util';
import { trackFormEvent } from 'src/util/trackFormEvent';

import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type { HNSkjemaFrontend } from 'src/types/Resources';

import { Breakpoint, useBreakpoint } from '@helsenorge/designsystem-react';
import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';

import FormButtonsMicrowebOppgave from './FormButtons-MicrowebOppgave';
import FormButtonsMicrowebStep from './FormButtons-MicrowebStep';
import FormButtonsStandard from './FormButtons-Standard';
import SkjemaLoginButton from '../skjemaLoginButton';
import FormButtonsStandardStepView from './FormButtons-StandardStepView';
import { CustomFormButtonType, useCustomFormButtonType } from './useCustomFormButtonType';

interface Props {
  reactHookFormMethods: UseFormReturn<FieldValues, unknown>;
  onCancel: () => void;
  onSubmit: () => void;
  onSave: () => void;
  nextStep: (() => void) | undefined;
  previousStep: (() => void) | undefined;
  displayNextButton: boolean | undefined;
  displayPreviousButton: boolean | undefined;
  setNewStepIndex?: (index: number) => void;
}

const RenderCustomFormButtons = ({
  reactHookFormMethods,
  onCancel,
  onSubmit,
  onSave,
  nextStep,
  previousStep,
  displayNextButton,
  displayPreviousButton,
  setNewStepIndex,
}: Props): React.JSX.Element => {
  const userMetadata = useAppSelector(state => state.refero.ui.userMetadata);
  const formButtonType = useCustomFormButtonType();
  const resources = useResources<HNSkjemaFrontend>(HN_SKJEMA_FRONTEND_RESOURCES);

  const erSendOgLagreKnapperDisabled = (): boolean => {
    const hideIfRepresentertSubjectOnly = userMetadata?.IsRepresentert && userMetadata?.CanOnlyBePerformedBySubject;
    const hideIfNoTilgang = !userMetadata?.HasTilgang;
    return isAuthorized() && (hideIfRepresentertSubjectOnly || hideIfNoTilgang);
  };

  const trackFormError = (): void => {
    trackFormEvent('site error', getSkjemaNameFromUrl());
  };

  const breakpoint = useBreakpoint();
  const displaySaveIcon = breakpoint > Breakpoint.xxs && formButtonType !== CustomFormButtonType.standardStepView;

  if (formButtonType === CustomFormButtonType.loginButton) {
    return (
      <div className={`formButtonsWrapper page_refero__buttons`}>
        <div className="page_refero__buttonwrapper page_refero__saveblock">
          <SkjemaLoginButton buttonText={resources?.skjemaLoginButton || 'Logg inn for å sende inn'} />
        </div>
      </div>
    );
  }

  return (
    <>
      {(formButtonType === CustomFormButtonType.standardView || formButtonType === CustomFormButtonType.microwebStandardView) && (
        <FormButtonsStandard
          reactHookFormMethods={reactHookFormMethods}
          onCancel={onCancel}
          onSubmit={onSubmit}
          onSave={onSave}
          trackFormError={trackFormError}
          erSendOgLagreKnapperDisabled={erSendOgLagreKnapperDisabled()}
          displaySaveIcon={displaySaveIcon}
        />
      )}
      {(formButtonType === CustomFormButtonType.standardStepView || formButtonType === CustomFormButtonType.microwebStandardStepView) && (
        <FormButtonsStandardStepView
          formButtonType={formButtonType}
          reactHookFormMethods={reactHookFormMethods}
          onCancel={onCancel}
          onSubmit={onSubmit}
          trackFormError={trackFormError}
          erSendOgLagreKnapperDisabled={erSendOgLagreKnapperDisabled()}
          nextStep={nextStep}
          previousStep={previousStep}
          displayNextButton={displayNextButton}
          displayPreviousButton={displayPreviousButton}
          setNewStepIndex={setNewStepIndex}
          onSave={onSave}
        />
      )}
      {formButtonType === CustomFormButtonType.microwebStepView && (
        <FormButtonsMicrowebStep
          reactHookFormMethods={reactHookFormMethods}
          trackFormError={trackFormError}
          erSendOgLagreKnapperDisabled={erSendOgLagreKnapperDisabled()}
          displaySaveIcon={displaySaveIcon}
          onSubmit={onSubmit}
        />
      )}
      {formButtonType === CustomFormButtonType.microwebOppgaveView && (
        <FormButtonsMicrowebOppgave
          reactHookFormMethods={reactHookFormMethods}
          onSubmit={onSubmit}
          trackFormError={trackFormError}
          erSendOgLagreKnapperDisabled={erSendOgLagreKnapperDisabled()}
        />
      )}
    </>
  );
};

export default RenderCustomFormButtons;
